import { useQuery } from '@tanstack/react-query'
import { ITEM_STATUS_LABELS_RU } from '@crm/shared'
import type { RequestItemStatus } from '@crm/shared'
import { getAddableOrderPositions } from '../../../shared/api/orders.api'
import { Spinner } from '../../../shared/ui/Spinner'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { EmptyState } from '../../../shared/ui/EmptyState'
import { formatDate, formatPrice } from '../orderFormat'

interface Props {
  orderId: string
  selected: Set<string>
  onToggle: (requestItemId: string) => void
}

/**
 * Вкладка «Из КП заявки» окна «Добавить позиции»: позиции заявки, которых нет
 * в заказе. Цена — из последнего КП, где позиция есть; без КП позицию отсюда
 * не добавить (подсказка ведёт на вкладку «Новая позиция»).
 */
export function AddFromKpList({ orderId, selected, onToggle }: Props) {
  const query = useQuery({
    queryKey: ['orders', orderId, 'addable-positions'],
    queryFn: () => getAddableOrderPositions(orderId),
  })

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }
  if (query.isError) return <ErrorMessage error={query.error} />
  const rows = query.data ?? []
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Все позиции заявки уже в заказе"
        description="Новую деталь добавьте на вкладке «Новая позиция»"
      />
    )
  }

  return (
    <ul className="max-h-[45vh] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
      {rows.map((r) => {
        const disabled = r.kpPrice == null
        return (
          <li key={r.requestItemId}>
            <label
              className={`flex items-start gap-3 px-3 py-2 text-sm ${
                disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer hover:bg-gray-50'
              }`}
              title={disabled ? 'Позиции нет ни в одном КП — добавьте её как новую позицию' : ''}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={selected.has(r.requestItemId)}
                disabled={disabled}
                onChange={() => onToggle(r.requestItemId)}
              />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-gray-900">
                  {r.position}. {r.partNumber}
                </span>
                <span className="text-gray-500">
                  {r.brand ? ` · ${r.brand}` : ''} · {r.quantity} {r.unit ?? 'шт'} ·{' '}
                  {ITEM_STATUS_LABELS_RU[r.status as RequestItemStatus] ?? r.status}
                </span>
              </span>
              <span className="whitespace-nowrap text-right text-xs">
                {r.kpPrice == null ? (
                  'нет в КП'
                ) : (
                  <>
                    <span className="font-medium text-gray-900">{formatPrice(r.kpPrice)}</span>
                    <span className="block text-gray-400">
                      КП от {formatDate(r.quotationCreatedAt)}
                    </span>
                  </>
                )}
              </span>
            </label>
          </li>
        )
      })}
    </ul>
  )
}
